import React, { ReactNode, useContext, useMemo } from 'react'
import {
  AccessAccount,
  AccessTeam,
  OnContributorsChange,
  TeamContributor,
  UserContributor,
} from './types'

export interface AccessProviderContext {
  users: AccessAccount[]
  ownerUsers?: AccessAccount[]
  teams: AccessTeam[]
  userContributors: UserContributor[]
  teamContributors: TeamContributor[]
  UserAvatar: React.ComponentType<{
    name: string
    accountId: string
    size?: number
    [key: string]: any
  }>
  onChange?: OnContributorsChange
}

const AccessContext = React.createContext<AccessProviderContext>({
  users: [],
  ownerUsers: undefined,
  teams: [],
  userContributors: [],
  teamContributors: [],
  UserAvatar: () => {
    return null
  },
  onChange: () => {},
})

const AccessProvider = ({
  children,
  users,
  ownerUsers,
  teams,
  userContributors,
  teamContributors,
  UserAvatar,
  onChange,
}: {
  children: ReactNode
  users: AccessProviderContext['users']
  ownerUsers?: AccessProviderContext['ownerUsers']
  teams: AccessProviderContext['teams']
  userContributors?: AccessProviderContext['userContributors']
  teamContributors?: AccessProviderContext['teamContributors']
  UserAvatar: AccessProviderContext['UserAvatar']
  onChange?: AccessProviderContext['onChange']
}) => {
  const contextValue = useMemo(
    () => ({
      users,
      ownerUsers,
      teams,
      userContributors,
      teamContributors,
      UserAvatar,
      onChange,
    }),
    [users, ownerUsers, teams, userContributors, teamContributors, UserAvatar, onChange]
  )

  return (
    <AccessContext.Provider value={contextValue}>
      {children}
    </AccessContext.Provider>
  )
}

export const useAccessContext = () => {
  const context = useContext(AccessContext)

  return context
}

export default AccessProvider
